import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/Form/FormField';
import React, { MouseEvent, useEffect, useState } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { useListAllCheckPoints } from '@/hooks/check-point.hooks';
import { TCheckPointSchema } from '@/schemas/check-point/check-point.schema';
import { Select } from '@/components/Controls/Select';
import Loader from '@/components/Loading/Loader';
import { CheckPointType } from '@/api/entities/check-point';
import { useRowDragAnDrop } from '@/hooks/drag-and-drop.hooks';
import { getTranslatedItem } from '@/utils/language.utils';
import {
  useCreateNewVehiculeCheckTypeCheckPoint,
  useDeleteVehiculeCheckTypeCheckPoint,
  useUpdateVehiculeCheckTypeCheckPoint,
} from '@/hooks/vehicule-check-type-check-point.hooks';
import { TVehiculeCheckTypeCheckPointSchema } from '@/schemas/vehicule-check-type-check-point/vehicule-check-type-check-point.schema';
import { getSortedList } from '@/hooks/list-order';
import { Checkbox } from '@/components/Controls/Checkbox';
import { ArrowDirection } from '@/api/entities/vehicule-check-type-check-point';
import Modal from '@/components/Modal';
import { Accordion } from '@/components/Accordion';
import { RichTextEditor } from '@/components/Custom/RichTextEditor';
import { showModal } from '@/utils/modal.utils';
import { containsOnlyEmptyHTMLTags } from '@/utils/string.utils';
import { twMerge } from 'tailwind-merge';
import { getVehiculeCheckTypeById } from '@/api/vehicule-check-type/vehicule-check-type';
import { useUserRole } from '@/hooks/auth.hooks';
import { useStoreDataOnDragAndDrop } from '@/hooks/drag-and-drop-store-data.hooks';

interface VehiculeCheckTypeCheckPointsProps<TSchema extends FieldValues>
  extends FormManagementProps<TSchema> {
  title: string | React.ReactNode;
  type: CheckPointType;
}

export const VehiculeCheckTypeCheckPoints = <TSchema extends FieldValues>({
  t,
  title,
  type,
  data,
  setValue,
  watch,
}: VehiculeCheckTypeCheckPointsProps<TSchema>) => {
  const { isAdmin } = useUserRole();

  const [selectedCheckPoint, setSelectedCheckPoint] =
    useState<TCheckPointSchema>();

  const [checkPointsToList, setCheckPointsToList] = useState<
    TCheckPointSchema[]
  >([]);

  const [
    selectedVehiculeCheckTypeCheckPoint,
    setSelectedVehiculeCheckTypeCheckPoint,
  ] = useState<TVehiculeCheckTypeCheckPointSchema>();

  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);

  const filteredVehiculeCheckTypeCheckPoints =
    data?.vehiculeCheckTypeCheckPoints?.filter((item) => item?.type === type);

  // useEffect(() => {
  //   if (filteredVehiculeCheckTypeCheckPoints?.length > 0) {
  //     setFirstCheckPointId(
  //       filteredVehiculeCheckTypeCheckPoints[0]?.checkPointId
  //     );
  //   }
  // }, [filteredVehiculeCheckTypeCheckPoints]);

  const { data: allCheckPoints, isLoading: isLoadingAllCheckPoints } =
    useListAllCheckPoints();

  const {
    mutate: createVehiculeCheckTypeCheckPoint,
    isPending: isPendingCreateVehiculeCheckTypeCheckPoint,
  } = useCreateNewVehiculeCheckTypeCheckPoint({
    handleSuccess: () => {
      setSelectedCheckPoint(undefined);
    },
  });

  const {
    mutate: updateVehiculeCheckTypeCheckPoint,
    isPending: isPendingUpdateVehiculeCheckTypeCheckPoint,
  } = useUpdateVehiculeCheckTypeCheckPoint({
    handleSuccess: () => {
      setSelectedCheckPoint(undefined);
      setSelectedVehiculeCheckTypeCheckPoint(undefined);

      // if (checkPointInformation) {
      //   notify(t('checkPointInformationSavedSuccessfully'), NOTIFY.SUCCESS);
      //   setCheckPointInformation('');
      // }
    },
  });

  const {
    mutate: deleteVehiculeCheckTypeCheckPoint,
    isPending: isPendingDeleteVehiculeCheckTypeCheckPoint,
  } = useDeleteVehiculeCheckTypeCheckPoint();

  const vehiculeCheckTypeCheckPointsValue =
    watch &&
    watch(
      'vehiculeCheckTypeCheckPoints' as Path<TSchema>,
      filteredVehiculeCheckTypeCheckPoints ?? []
    );

  const isLoading =
    isLoadingAllCheckPoints ||
    isPendingCreateVehiculeCheckTypeCheckPoint ||
    isPendingUpdateVehiculeCheckTypeCheckPoint ||
    isPendingDeleteVehiculeCheckTypeCheckPoint ||
    deletingItemId !== null;

  const {
    rowData,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useRowDragAnDrop({
    data: vehiculeCheckTypeCheckPointsValue,
    setValue,
    dragIdentifierField: 'checkPointId',
    fieldName: 'vehiculeCheckTypeCheckPoints',
    // onReorder: (reorderedItems) => {
    //   reorderedItems.forEach((item) => {
    //     updateVehiculeCheckTypeCheckPoint({ ...item });
    //   });
    // },
  });

  useStoreDataOnDragAndDrop({
    rowData,
    data,
    mutate: updateVehiculeCheckTypeCheckPoint,
    fieldName: 'index',
    updateIndividualItems: true,
  });

  useEffect(() => {
    if (setValue && data) {
      (async () => {
        const response = await getVehiculeCheckTypeById(data?.id);
        setValue(
          'vehiculeCheckTypeCheckPoints' as Path<TSchema>,
          response?.data?.vehiculeCheckTypeCheckPoints ?? []
        );
      })();
    }
  }, [setValue, data]);

  // useEffect(() => {
  //   if (setValue && data) {
  //     setValue(
  //       'vehiculeCheckTypeCheckPoints' as Path<TSchema>,
  //       data?.vehiculeCheckTypeCheckPoints ?? []
  //     );
  //   }
  // }, [setValue, data]);

  useEffect(() => {
    const checkPoints = allCheckPoints?.data?.filter(
      (checkPoint) =>
        checkPoint?.type === type &&
        !vehiculeCheckTypeCheckPointsValue?.some(
          (item) => item?.checkPointId === checkPoint?.id
        )
    );

    setCheckPointsToList(checkPoints);
  }, [allCheckPoints, type, vehiculeCheckTypeCheckPointsValue]);

  // Clear deleting state when data changes (indicating delete completed)
  useEffect(() => {
    if (deletingItemId && filteredVehiculeCheckTypeCheckPoints) {
      const itemStillExists = filteredVehiculeCheckTypeCheckPoints.some(
        (item) => (item.id || item.checkPointId) === deletingItemId
      );
      if (!itemStillExists) {
        setDeletingItemId(null);
      }
    }
  }, [filteredVehiculeCheckTypeCheckPoints, deletingItemId]);

  const getCheckPointsSelectOptions = () => {
    if (checkPointsToList?.length > 0) {
      return checkPointsToList.map((checkPoint: TCheckPointSchema) => {
        const { id, name } = checkPoint;

        const translatedCheckPoint = getTranslatedItem(
          checkPoint?.translatedCheckPoints
        );

        return {
          label: translatedCheckPoint?.name || name,
          value: id,
        };
      });
    }

    return undefined;
  };

  const onSelectCheckPoint = (checkPointId: string) => {
    const checkPoint = allCheckPoints?.data?.find(
      (checkPoint) => checkPoint?.id === checkPointId
    );
    setSelectedCheckPoint(checkPoint);
  };

  const onAddCheckPoint = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const vehiculeCheckTypeCheckPoint = vehiculeCheckTypeCheckPointsValue?.find(
      (item) => item?.checkPointId === selectedCheckPoint?.id
    );

    if (!vehiculeCheckTypeCheckPoint) {
      const addedVehiculeCheckTypeCheckPoints = [
        ...vehiculeCheckTypeCheckPointsValue,
        {
          vehiculeCheckTypeId: data?.id,
          checkPointId: selectedCheckPoint?.id,
          type,
        },
      ];
      setValue(
        'vehiculeCheckTypeCheckPoints' as Path<TSchema>,
        addedVehiculeCheckTypeCheckPoints as PathValue<TSchema, Path<TSchema>>
      );

      createVehiculeCheckTypeCheckPoint({
        vehiculeCheckTypeId: data?.id,
        checkPointId: selectedCheckPoint?.id,
        type,
      });
    }
  };

  const onCheckUseArrow = (
    vehiculeCheckTypeCheckPoint: TVehiculeCheckTypeCheckPointSchema,
    checked: boolean
  ) => {
    let arrowDirection = vehiculeCheckTypeCheckPoint?.arrowDirection;

    if (!checked) {
      arrowDirection = null;
    } else if (!arrowDirection) {
      arrowDirection = ArrowDirection.TOP;
    }

    updateVehiculeCheckTypeCheckPoint({
      ...vehiculeCheckTypeCheckPoint,
      useArrow: checked,
      arrowDirection,
    });
  };

  const onCheckUseInformation = (
    vehiculeCheckTypeCheckPoint: TVehiculeCheckTypeCheckPointSchema,
    checked: boolean
  ) => {
    let information = vehiculeCheckTypeCheckPoint?.information;

    if (!checked) {
      information = null;
    } else if (!information) {
      information = `${t('yourText')} ...`;
    }

    updateVehiculeCheckTypeCheckPoint({
      ...vehiculeCheckTypeCheckPoint,
      useInformation: checked,
      information,
    });
  };

  const onClickEditInformation = (
    checkPoint: TCheckPointSchema,
    vehiculeCheckTypeCheckPoint: TVehiculeCheckTypeCheckPointSchema
  ) => {
    setSelectedCheckPoint(checkPoint);
    setSelectedVehiculeCheckTypeCheckPoint(vehiculeCheckTypeCheckPoint);
    showModal(`#information-modal-${type}`);
  };

  const onRemoveCheckPoint = (
    vehiculeCheckTypeCheckPoint: TVehiculeCheckTypeCheckPointSchema
  ) => {
    setDeletingItemId(
      vehiculeCheckTypeCheckPoint.id || vehiculeCheckTypeCheckPoint.checkPointId
    );
    deleteVehiculeCheckTypeCheckPoint(vehiculeCheckTypeCheckPoint);
  };

  const onSaveInformation = () => {
    updateVehiculeCheckTypeCheckPoint(selectedVehiculeCheckTypeCheckPoint);
  };

  const renderAddedCheckpoints = () => {
    return getSortedList(
      rowData as TVehiculeCheckTypeCheckPointSchema[],
      'index',
      'asc'
    )?.map((item) => {
      const useArrow = item?.useArrow;
      const useInformation = item?.useInformation;

      const checkPoint = allCheckPoints?.data?.find(
        (checkPoint) =>
          checkPoint?.id === item?.checkPointId && checkPoint?.type === type
      );

      const translatedCheckPoint = getTranslatedItem(
        checkPoint?.translatedCheckPoints
      );

      if (checkPoint) {
        return (
          <Accordion
            key={item?.checkPointId}
            triggerWrapperClassName="px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-lg hover:cursor-grab"
            // defaultIconClassName="mt-1.5 text-slate-500 text-[20px]"
            // defaultIconClassName="mt-1.5 text-slate-800 text-[20px]"
            // defaultIconClassName="text-gray-800/50"
            // defaultIconClassName="text-[#ff0a54] font-medium"
            defaultIconClassName="text-[var(--purple-custom)] font-medium"
            dragIconClassName="text-slate-500/80 text-[16px]"
            titleClassName="mt-0.75 text-slate-700 font-semibold line-clamp-1"
            bodyContainerClassName="-mt-[8px] border-r border-b border-l border-slate-200 bg-slate-100 rounded-bl-lg rounded-br-lg"
            title={translatedCheckPoint?.name || checkPoint?.name}
            // active={firstCheckPointId === item?.checkPointId}
            linkForEdit={
              isAdmin ? `/check-point/edit/${item?.checkPointId}` : undefined
            }
            onDelete={() => onRemoveCheckPoint(item)}
            onDeleteDisabled={isLoading}
            draggable
            onDragStart={(event) => handleDragStart(event, item?.checkPointId)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(event) => handleDrop(event, item?.checkPointId)}
          >
            <div className="bg-white py-3 px-4 rounded-lg">
              <div className="flex items-center gap-3">
                <Checkbox
                  containerClassName="flex-initial"
                  innerWrapperClassName="gap-1.5"
                  checkboxClassName="!w-[17px] !h-[17px]"
                  textClassName="mt-0.5 py-0.75 text-slate-500 font-medium"
                  text={t('withArrow')}
                  checked={useArrow || false}
                  onChange={(e) => {
                    onCheckUseArrow(item, e.target.checked);
                  }}
                />
                <div className="text-slate-300 font-extralight">|</div>
                <Checkbox
                  containerClassName="flex-initial"
                  innerWrapperClassName="gap-1.5"
                  checkboxClassName="!w-[17px] !h-[17px]"
                  textClassName="mt-0.5 py-0.75 text-slate-500 font-medium"
                  text={t('withInformation')}
                  checked={useInformation || false}
                  onChange={(e) => {
                    onCheckUseInformation(item, e.target.checked);
                  }}
                />
              </div>
              {(useArrow || useInformation) && (
                <div className="mt-3.5 pt-3 pb-4.5 px-4 flex items-center gap-5 border border-dashed border-slate-300 rounded-md">
                  {useArrow && (
                    <div>
                      <div className="text-2sm text-gray-600 font-medium mb-1">
                        {t('directionOfTheArrow')}{' '}
                        <span className="text-red-FF2929/90 text-[17px] font-normal">
                          *
                        </span>{' '}
                        :
                      </div>
                      <Select
                        selectClassName="w-[180px]"
                        // placeholder={t('chooseArrowDirection')}
                        options={[
                          {
                            label: t('common:top'),
                            value: ArrowDirection.TOP,
                          },
                          {
                            label: t('common:bottom'),
                            value: ArrowDirection.BOTTOM,
                          },
                          {
                            label: t('common:left'),
                            value: ArrowDirection.LEFT,
                          },
                          {
                            label: t('common:right'),
                            value: ArrowDirection.RIGHT,
                          },
                        ]}
                        value={item?.arrowDirection}
                        onChange={(e) => {
                          updateVehiculeCheckTypeCheckPoint({
                            ...item,
                            arrowDirection: e.currentTarget
                              .value as ArrowDirection,
                          });
                        }}
                      />
                      {/* <ErrorMessage
                        className="mt-1 text-xs font-medium"
                        message="Veuillez choisir la direction de la flèche"
                      /> */}
                    </div>
                  )}
                  {useInformation && (
                    // <div className="border-l border-slate-200 pl-5">
                    <div
                      className={twMerge(
                        'relative pl-5 after:absolute after:content-[""] after:left-0 after:top-[4.8px] after:bottom-0 after:w-[1px] after:h-full after:bg-slate-200/80',
                        !useArrow && 'pl-0 static'
                      )}
                    >
                      <div className="text-2sm text-gray-600 font-medium mb-1">
                        {t('common:information')}{' '}
                        <span className="text-red-FF2929/90 text-[17px] font-normal">
                          *
                        </span>{' '}
                        :
                      </div>
                      <Button
                        leftIcon={
                          <i className="ki-outline ki-notepad-edit text-[var(--purple-custom)] !text-[16px] font-medium" />
                        }
                        buttonClassName="h-8 px-3 bg-white border-[var(--purple-custom)] transition-all duration-200"
                        textClassName="mt-[1.5px] text-xs text-[var(--purple-custom)] font-medium"
                        text={t('editInformation')}
                        onClick={() => {
                          onClickEditInformation(checkPoint, item);
                        }}
                      />
                      {/* <ErrorMessage
                        className="mt-1 text-xs font-medium"
                        message="Veuillez saisir une information"
                      /> */}
                    </div>
                  )}
                  {/* <div className="flex flex-1 justify-end">
                    <Button textClassName="text-xs" text="Sauvegarder" />
                  </div> */}
                </div>
              )}
            </div>
          </Accordion>
        );
      }

      return <React.Fragment key={item?.checkPointId}></React.Fragment>;
    });
  };

  return (
    <>
      <Card title={title} bodyClassName="py-8 flex flex-col gap-5">
        <div className="flex flex-1 justify-center">
          <div className="flex flex-col items-center gap-8">
            {isLoadingAllCheckPoints ? (
              <></>
            ) : checkPointsToList?.length === 0 ? (
              <div className="self-start -mb-4 flex gap-2">
                <i className="h-0 -mt-[4px] ki-filled ki-information-1 text-xl text-red-FF2929/90" />
                <p className="text-red-FF2929/90 text-sm font-semibold">
                  {t('noCheckPointToAdd')}
                </p>
              </div>
            ) : (
              <></>
            )}
            <FormField
              text={`${t('common:checkPoint')} :`}
              textOuterWrapperClassName="w-auto"
              textWrapperClassName="mt-2.5"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <div>
                  <Select
                    selectClassName="w-[255px]"
                    placeholder={t('common:chooseTheCheckPoint')}
                    options={getCheckPointsSelectOptions()}
                    value={selectedCheckPoint?.id}
                    onChange={(e) => {
                      onSelectCheckPoint(e.currentTarget.value);
                    }}
                  />
                </div>
                <div>
                  <Button
                    text={t('common:add')}
                    buttonClassName="disabled:bg-[--tw-gray-600] px-4"
                    disabled={
                      !selectedCheckPoint?.id ||
                      checkPointsToList?.length === 0 ||
                      isPendingCreateVehiculeCheckTypeCheckPoint
                    }
                    leftIcon={<i className="ki-filled ki-plus-circle" />}
                    onClick={onAddCheckPoint}
                  />
                </div>
              </div>
            </FormField>
            <div className="w-full">
              {/* {filteredVehiculeCheckTypeCheckPoints?.length > 0 && (
                <div className="text-sm text-gray-600 mb-4">
                  {`${t('clickOnCheckPointToConfigure')} :`}
                </div>
              )} */}
              <div className="flex flex-col gap-5">
                {renderAddedCheckpoints()}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Modal
        id={`information-modal-${type}`}
        title={`${t('common:information')} : ${
          getTranslatedItem(selectedCheckPoint?.translatedCheckPoints)?.name ||
          selectedCheckPoint?.name ||
          ''
        }`}
        centerY
        showSecondBtn={true}
        secondBtnLabel={t('common:save')}
        size="large"
        bodyClassName="w-full"
        disabledSubmitButton={containsOnlyEmptyHTMLTags(
          selectedVehiculeCheckTypeCheckPoint?.information || ''
        )}
        onClickSecondBtn={onSaveInformation}
      >
        <div className="px-4 py-2.75 bg-slate-100 rounded-lg text-gray-700 text-md font-medium mb-4">
          {`${t('pleaseEnterCheckPointInformation')} "${
            getTranslatedItem(selectedCheckPoint?.translatedCheckPoints)
              ?.name ||
            selectedCheckPoint?.name ||
            ''
          }" :`}
        </div>
        <div>
          <RichTextEditor
            value={selectedVehiculeCheckTypeCheckPoint?.information}
            onChange={(value) => {
              setSelectedVehiculeCheckTypeCheckPoint((prevState) => ({
                ...prevState,
                information: value,
              }));
            }}
          />
        </div>
      </Modal>
      {isLoading && <Loader />}
    </>
  );
};
